import { ref } from 'vue';

export const useLoadWithRetry = (config = {}) => {
  const maxRetry = config.max_retry || 3;
  const backoff = config.backoff || 1000;
  // Aceleres 2026-09-09: teto por tentativa. Sem ele, um pedido que fica pendurado
  // (sem `load` e sem `error` — ligacao presa, Active Storage a demorar) nunca
  // resolvia nem rejeitava, e a bolha ficava no esqueleto cinzento PARA SEMPRE:
  // era o "fica a carregar ate atualizar a pagina" relatado. Com o teto, a
  // tentativa falha, o retry corre, e no fim o utilizador ve o erro com
  // "Tentar novamente" em vez de um placeholder eterno.
  const attemptTimeout = config.attempt_timeout || 20000;

  const isLoaded = ref(false);
  const hasError = ref(false);

  // Aceleres 2026-09-09: os refs sao partilhados entre chamadas, e as tentativas
  // sao assincronas. Sem esta geracao, o retry de um URL ANTIGO ainda em curso
  // escrevia por cima do estado do URL novo — o caso mais visivel era a bolha
  // que ja tinha desenhado a imagem voltar a "Nao foi possivel carregar" quando
  // a ultima tentativa do URL anterior esgotava. Cada chamada leva a sua
  // geracao; quem nao e a geracao corrente nao toca nos refs.
  let generation = 0;

  const resetLoadState = () => {
    generation += 1;
    isLoaded.value = false;
    hasError.value = false;
  };

  const loadWithRetry = async url => {
    generation += 1;
    const myGeneration = generation;
    const isCurrent = () => myGeneration === generation;

    const attemptLoad = () => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        let settled = false;
        let timeoutId = null;

        const finish = fn => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeoutId);
          fn();
        };

        timeoutId = window.setTimeout(() => {
          finish(() => {
            // Cortar o `src` interrompe o pedido pendurado em vez de o deixar a
            // ocupar uma das conexoes do dominio ate o navegador desistir.
            img.src = '';
            reject(new Error('Image load timed out'));
          });
        }, attemptTimeout);

        img.onload = () =>
          finish(() => {
            if (isCurrent()) {
              isLoaded.value = true;
              hasError.value = false;
            }
            resolve({
              width: img.naturalWidth,
              height: img.naturalHeight,
            });
          });

        img.onerror = () =>
          finish(() => reject(new Error('Failed to load image')));

        img.src = url;
      });
    };

    const sleep = ms => {
      return new Promise(resolve => {
        setTimeout(resolve, ms);
      });
    };

    const retry = async (attempt = 0) => {
      try {
        return await attemptLoad();
      } catch (error) {
        if (!isCurrent()) return undefined;
        if (attempt + 1 >= maxRetry) {
          hasError.value = true;
          isLoaded.value = false;
          return undefined;
        }
        await sleep(backoff * (attempt + 1));
        if (!isCurrent()) return undefined;
        return retry(attempt + 1);
      }
    };

    return retry();
  };

  return {
    isLoaded,
    hasError,
    loadWithRetry,
    resetLoadState,
  };
};
